import type { Block } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import { OverlappingRoomsError } from "building-types/errors/buildingGenerationError";
import type {
  Facade,
  FacadeSpanStratum,
  FacadeSweepStratum,
  GeneratedFacade,
} from "building-types/facades/facadeTypes";
import type { Label } from "building-types/facades/label";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { FoundationAssemblyEntity } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationAssemblyOverrides } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import type { FoundationPattern } from "building-types/foundations/foundationPatternTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { Level } from "building-types/levels/levelTypes";
import type {
  HalfEdgeAssociationTable,
  PlanarGraphLevelAssociations,
} from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { RoomConnection } from "building-types/rooms/roomTraversalTypes";
import { RoomConnectionType } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomClass, RoomCover, RoomEnclosure, RoomKind, RoomSupport } from "building-types/rooms/roomTypesEnum";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { FoundationSettings } from "building-types/settings/foundationSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import type { WallAssemblyLayerOperator, WallAssemblyOperator } from "building-types/walls/wallAssemblyOverrideTypes";
import type { WallSideRoomDescriptor } from "building-types/walls/wallDesciptorTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallCondition } from "building-types/walls/wallType";
import type { WallPath } from "building-types/walls/wallTypes";
import type { RWTable, Table } from "common/data/table";
import { voidFloorAssemblyNew } from "doc/elements/floor/floorAssembly";
import { finalizeFoundationComponents } from "doc/elements/foundation/foundationComponentsTools";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { isInteriorRoomEnclosure, isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { isExteriorWallCondition } from "doc/utils/wallTypeTools";
import { aabb2ByPolyLine2s, aabb2ByTwoPoints, aabb2Overlaps } from "geom/aabb2";
import { WindingDir } from "geom/core";
import { line2Len } from "geom/line2";
import type { PlanarGraph } from "geom/planarGraph";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2EdgesCollinearToLine } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { groundLevelId } from "../elements/level/levelTools";
import { collectRoomConnectionsOnLevel } from "../elements/rooms/collectRoomConnectionsOnLevel";
import { wallAssemblyInteriorFinishLayerNew } from "../elements/wallAssemblies/wallAssembly";
import { wallAssemblyLayerOperatorOverrideNew } from "../elements/wallAssemblies/wallAssemblyOverride";
import { determineWallCondition } from "../elements/walls2/wallConditionTools";
import { wallPathNew } from "../elements/walls/wall";
import { wallPathOpposite } from "../elements/walls/wallDescriptor";
import { deprecatedBaseOffset, makeWallDescriptor } from "../elements/walls/wallDescriptor";
import { getLevelSubRoofPerimeters } from "../generationTools/generateWallMassesTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  levels,
  doc: { rooms: docRooms },
  rooms,
  blocks,
  optionChoices,
  doc,
  roomLevelIndices,
  roomsByLevel,
  roomFloorAssemblies,
  planarGraphs,
  openFullDoors,
  wallAssemblyOverrides,
  settings,
  planarGraphLevelAssociations,
  roofSystemLevels,
  foundationAssemblyOverrides,
  foundationPatterns,
  facades,
  labels,
  facadeSpanStrata,
  facadeSweepStrata,
  datumAbstractions,
}: Building) {
  return {
    optionChoices: resolveOptionsInFormula({ foundationAssemblyOverrides }, doc, optionChoices),
    levels,
    docRooms,
    rooms,
    blocks,
    roomLevelIndices,
    roomsByLevel,
    roomFloorAssemblies,
    planarGraphs,
    openFullDoors,
    wallSettings: settings.wall,
    wallAssemblyOverrides,
    planarGraphLevelAssociations,
    roofSystemLevels,
    foundationSettings: settings.foundation,
    foundationComponents: doc.foundationComponents,
    foundationAssemblies: doc.foundationAssemblies,
    foundationAssemblyOverrides,
    foundationPatterns,
    framingComponents: doc.framingComponents,
    slabComponents: doc.slabComponents,
    sheathingComponents: doc.sheathingComponents,
    datumAbstractions,
    facades,
    labels,
    facadeSweepStratum: facadeSweepStrata,
    facadeSpanStratum: facadeSpanStrata,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  openFullDoors: "deep",
});

function generate({
  optionChoices,
  levels,
  docRooms,
  rooms,
  blocks,
  roomLevelIndices,
  roomsByLevel,
  roomFloorAssemblies,
  planarGraphs,
  openFullDoors,
  wallSettings,
  wallAssemblyOverrides,
  planarGraphLevelAssociations,
  roofSystemLevels,
  foundationSettings,
  foundationComponents,
  foundationAssemblies,
  foundationAssemblyOverrides,
  foundationPatterns,
  framingComponents,
  slabComponents,
  sheathingComponents,
  datumAbstractions,
  facades,
  labels,
  facadeSpanStratum,
  facadeSweepStratum,
}: ReturnType<typeof select>): {
  wallPaths: Table<WallPath[]>;
} {
  const finalizedFoundationComponents = finalizeFoundationComponents(
    foundationComponents,
    foundationSystemSettingsFromSettingsConfig(foundationSettings),
  );

  const evaluatedFoundationAssemblyOverrides: HalfEdgeAssociationTable<FoundationAssemblyOverrides> = evalFormulas(
    foundationAssemblyOverrides,
    (v) => optionChoices[v],
  );

  const wallPaths: RWTable<WallPath[]> = {};

  // we need to process the ground level first as reference
  const refLevelId = groundLevelId(levels);
  if (refLevelId) {
    const level = levels[refLevelId];
    const planarGraph = planarGraphs[refLevelId];

    const roomConnections = collectRoomConnectionsOnLevel({
      openFullDoors,
      levelId: refLevelId,
      roomsByLevel,
      roomLevelIndices,
    });

    const levelWallAssemblyOverrides = wallAssemblyOverrides[refLevelId];
    const levelLabels = labels[refLevelId];
    const roofLevelPerimeters = getLevelSubRoofPerimeters(refLevelId, levels, roofSystemLevels, wallSettings);

    wallPaths[refLevelId] = makeWallPathsForLevel({
      levelId: refLevelId,
      levels,
      docRooms,
      rooms,
      roomsByLevel,
      roomLevelIndices,
      roomConnections,
      roomFloorAssemblies,
      planarGraph,
      planarGraphLevelAssociations: planarGraphLevelAssociations[refLevelId],
      wallSettings,
      foundationSettings,
      foundationComponents: finalizedFoundationComponents,
      foundationAssemblies,
      foundationAssemblyOverrides: evaluatedFoundationAssemblyOverrides[level.id],
      foundationPatterns: foundationPatterns[level.id],
      framingComponents,
      slabComponents,
      sheathingComponents,
      datumAbstractions,
      facades,
      levelLabels,
      facadeSpanStratum,
      facadeSweepStratum,
      wallAssemblyOverrides: levelWallAssemblyOverrides,
      blocks,
      roofLevelPerimeters,
    });
  }

  for (const levelId in levels) {
    if (levelId !== refLevelId) {
      const level = levels[levelId];
      const planarGraph = planarGraphs[levelId];

      const roomConnections = collectRoomConnectionsOnLevel({
        openFullDoors,
        levelId,
        roomsByLevel,
        roomLevelIndices,
      });

      const levelWallAssemblyOverrides = wallAssemblyOverrides[levelId];
      const levelLabels = labels[levelId];
      const roofLevelPerimeters = getLevelSubRoofPerimeters(levelId, levels, roofSystemLevels, wallSettings);

      wallPaths[levelId] = makeWallPathsForLevel({
        levelId,
        levels,
        docRooms,
        rooms,
        roomsByLevel,
        roomLevelIndices,
        roomConnections,
        roomFloorAssemblies,
        planarGraph,
        planarGraphLevelAssociations: planarGraphLevelAssociations[levelId],
        wallSettings,
        foundationSettings,
        foundationComponents: finalizedFoundationComponents,
        foundationAssemblies,
        foundationAssemblyOverrides: evaluatedFoundationAssemblyOverrides[level.id],
        foundationPatterns: foundationPatterns[level.id],
        framingComponents,
        slabComponents,
        sheathingComponents,
        datumAbstractions,
        facades,
        levelLabels,
        facadeSpanStratum,
        facadeSweepStratum,
        wallAssemblyOverrides: levelWallAssemblyOverrides,
        blocks,
        roofLevelPerimeters,
      });
    }
  }

  return { wallPaths };
}

export default memo(select, generate, compare);

interface WallPathLookupDataStructures {
  rooms: Room[];
  planarGraphFaceToRoom: number[];
  roomToPlanarGraphFace: number[];
  halfEdgeToRoomConnection: { [halfEdge: number]: RoomConnectionType };
  halfEdgeToFacades: { [halfEdge: number]: Facade[] };
}

function initWallLookupStructures({
  rooms,
  planarGraph: polygonPlanarGraph,
  roomConnections,
  roomLevelIndices,
  levelLabels,
  facades,
}: {
  rooms: Room[];
  planarGraph: PolygonPlanarGraph;
  roomConnections: RoomConnection[][];
  roomLevelIndices: Table<number>;
  levelLabels: HalfEdgeAssociationTable<Label>;
  facades: Table<GeneratedFacade>;
}): WallPathLookupDataStructures {
  const { polygonToPlanarGraph } = polygonPlanarGraph;
  const halfEdgeToRoomConnection: { [halfEdgeIndex: number]: RoomConnectionType } = {};

  for (let i = 0, l = roomConnections.length; i < l; i++) {
    const conns = roomConnections[i];

    for (let j = 0, lj = conns.length; j < lj; j++) {
      const { connectionType, halfEdge } = conns[j];
      if (connectionType === RoomConnectionType.OPEN || connectionType === RoomConnectionType.OPEN_EXTERIOR) {
        halfEdgeToRoomConnection[halfEdge] = connectionType;
      }
    }
  }

  const planarGraphFaceToRoom: number[] = [];
  const roomToPlanarGraphFace: number[] = [];
  for (let i = 0, l = rooms.length; i < l; i++) {
    const { id } = rooms[i];
    const roomIndexOnLevel = roomLevelIndices[id];
    const roomEdge = polygonToPlanarGraph[roomIndexOnLevel][0][0];
    planarGraphFaceToRoom[roomEdge.index] = roomIndexOnLevel;
    roomToPlanarGraphFace[roomIndexOnLevel] = roomEdge.index;
  }

  const halfEdgeToFacades: { [halfEdge: number]: Facade[] } = {};
  const halfEdgeIndices: { [index: number]: boolean } = {};
  for (const halfEdgeIndex in levelLabels) {
    halfEdgeIndices[halfEdgeIndex] = true;
  }

  for (const halfEdgeIndex of Object.keys(halfEdgeIndices)) {
    const halfEdgeLabels = levelLabels?.[+halfEdgeIndex] ?? [];
    const halfEdgeFacades: Facade[] = [];
    for (const label of halfEdgeLabels) {
      const { facadeId } = label.item;
      if (facades[facadeId]) {
        halfEdgeFacades.push(facades[facadeId]);
      }
    }

    halfEdgeToFacades[halfEdgeIndex] = halfEdgeFacades;
  }

  return {
    rooms,
    planarGraphFaceToRoom,
    roomToPlanarGraphFace,
    halfEdgeToRoomConnection,
    halfEdgeToFacades,
  };
}

function getRoomKind(
  rooms: Room[],
  planarGraph: PlanarGraph,
  roomIndex: number,
  planarGraphFaceIndex: number,
): RoomKind {
  if (roomIndex === undefined || planarGraph.faceWindingDirs[planarGraphFaceIndex] === WindingDir.CW) {
    return RoomKind.BOUNDARY;
  }

  const room = rooms[roomIndex];
  if (!isInteriorRoomEnclosure[room.attributes.roomEnclosure]) {
    return RoomKind.EXTERIOR;
  }

  return RoomKind.INTERIOR;
}

function getRoomEnclosure(rooms: Room[], roomIndex: number): RoomEnclosure {
  if (roomIndex === undefined || !rooms[roomIndex]) {
    return RoomEnclosure.NONE;
  }

  const room = rooms[roomIndex];
  return room.attributes.roomEnclosure;
}

function getRoomSupport(
  rooms: Room[],
  planarGraphLevelAssociations: PlanarGraphLevelAssociations,
  roomIndex: number,
  planarGraphFaceIndex: number,
): RoomSupport {
  if (roomIndex === undefined || !rooms[roomIndex]) {
    return RoomSupport.UNSUPPORTED;
  }
  const roomIsSupported = !!planarGraphLevelAssociations.facesBelow[planarGraphFaceIndex]?.length;
  if (roomIsSupported) {
    return RoomSupport.SUPPORTED;
  }

  return RoomSupport.UNSUPPORTED;
}

function getRoomCover(
  rooms: Room[],
  planarGraphLevelAssociations: PlanarGraphLevelAssociations,
  roomIndex: number,
  planarGraphFaceIndex: number,
): RoomCover {
  if (roomIndex === undefined || !rooms[roomIndex]) {
    return RoomCover.UNCOVERED;
  }
  const roomIsCovered = !!planarGraphLevelAssociations.facesAbove[planarGraphFaceIndex]?.length;
  if (roomIsCovered) {
    return RoomCover.COVERED;
  }

  return RoomCover.UNCOVERED;
}

function makeWallSideRoomDescriptor(
  levelId: string,
  halfEdge: number,
  planarGraph: PolygonPlanarGraph,
  planarGraphLevelAssociations: PlanarGraphLevelAssociations,
  planarGraphLookupTables: WallPathLookupDataStructures,
  blocks: Table<Block>,
): WallSideRoomDescriptor {
  const { halfEdgeParentFace } = planarGraph.planarGraph;
  const { rooms, halfEdgeToRoomConnection, planarGraphFaceToRoom } = planarGraphLookupTables;
  const faceIndex = halfEdgeParentFace[halfEdge];
  const roomIndex = planarGraphFaceToRoom[faceIndex];

  // lookup room (not all planar graph faces are from a room e.g. boundary face)
  const room = rooms[roomIndex];
  const roomClass = room?.attributes.roomClass ?? RoomClass.EXTERIOR;
  const roomKind = getRoomKind(rooms, planarGraph.planarGraph, roomIndex, faceIndex);
  const roomSupport = getRoomSupport(rooms, planarGraphLevelAssociations, roomIndex, faceIndex);
  const roomCover = getRoomCover(rooms, planarGraphLevelAssociations, roomIndex, faceIndex);
  const roomEnclosure = getRoomEnclosure(rooms, roomIndex);
  const roomConnection = halfEdgeToRoomConnection[halfEdge];
  const roomHalfStory = blocks[room?.parentId]?.isHalfStory;

  let roomEdgeIndex: number | undefined = undefined;
  const roomWallOperators: WallAssemblyLayerOperator[] = [];
  if (room) {
    roomEdgeIndex = planarGraph.halfEdgeIndexToPolygonEdgeIndex(halfEdge);
    if (room.wallMaterial) {
      roomWallOperators.push(
        wallAssemblyLayerOperatorOverrideNew(
          wallAssemblyInteriorFinishLayerNew({
            roomId: room.id,
            finishMaterial: room.wallMaterial,
            layerSource: {
              type: "ROOM",
              id: room.id,
            },
          }),
        ),
      );
    }
  }

  return {
    roomLevelId: levelId,
    room,
    roomEdgeIndex,
    roomClass,
    roomCover,
    roomEnclosure,
    roomSupport,
    roomKind,
    roomConnection,
    roomHalfStory,
    roomWallOperators,
  };
}

function getRoomFloorAssembly(roomFloorAssemblies: Table<FloorAssembly>, room?: Room): FloorAssembly {
  if (!room || !roomFloorAssemblies[room.id]) {
    return voidFloorAssemblyNew();
  }

  return roomFloorAssemblies[room.id];
}

function isEdgeHalfStoryUnderRoofWall(
  blocks: Table<Block>,
  leftRoom: Room | undefined,
  rightRoom: Room | undefined,
  roofLevelPerimeters: PolyLine2[],
  startX: number,
  startY: number,
  endX: number,
  endY: number,
): boolean {
  const isLeftHalfStory = leftRoom ? blocks[leftRoom.parentId].isHalfStory ?? false : true;
  const isRightHalfStory = rightRoom ? blocks[rightRoom.parentId].isHalfStory ?? false : true;

  let isHalfStoryUnderRoofWall = false;

  if (isLeftHalfStory && isRightHalfStory) {
    let isExteriorEndWall = false;
    let atLeastOneOverlap = false;
    for (const exactPerimeter of roofLevelPerimeters) {
      const edgeBB = aabb2ByTwoPoints(startX, startY, endX, endY);
      const exactPerimeterBB = aabb2ByPolyLine2s([exactPerimeter]);

      if (!exactPerimeterBB || !aabb2Overlaps(edgeBB, exactPerimeterBB)) {
        continue;
      }
      atLeastOneOverlap = true;

      const edgeIndices = polyLine2EdgesCollinearToLine(exactPerimeter, startX, startY, endX, endY, 0.05, 0.05);
      if (edgeIndices.length === 0) {
        continue;
      }
      isExteriorEndWall = true;
      break;
    }

    if (!isExteriorEndWall && atLeastOneOverlap) {
      isHalfStoryUnderRoofWall = true;
    }
  }
  return isHalfStoryUnderRoofWall;
}

export function makeWallPathsForLevel({
  levelId,
  levels,
  docRooms,
  rooms,
  roomsByLevel,
  roomLevelIndices,
  roomConnections,
  roomFloorAssemblies,
  planarGraph,
  planarGraphLevelAssociations,
  wallSettings,
  foundationSettings,
  foundationComponents,
  foundationAssemblies,
  foundationAssemblyOverrides,
  foundationPatterns,
  framingComponents,
  slabComponents,
  sheathingComponents,
  datumAbstractions,
  facades,
  levelLabels,
  facadeSpanStratum,
  facadeSweepStratum,
  wallAssemblyOverrides,
  blocks,
  roofLevelPerimeters,
}: {
  levelId: string;
  levels: Table<Level>;
  docRooms: Table<StaticRoom>;
  rooms: Table<Room>;
  roomsByLevel: Table<string[]>;
  roomLevelIndices: Table<number>;
  roomConnections: RoomConnection[][];
  roomFloorAssemblies: Table<FloorAssembly>;
  planarGraph: PolygonPlanarGraph;
  planarGraphLevelAssociations: PlanarGraphLevelAssociations;
  wallSettings: WallSettings;
  foundationSettings: FoundationSettings;
  foundationComponents: Table<FoundationComponentEntity>;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  foundationAssemblyOverrides: HalfEdgeAssociationTable<FoundationAssemblyOverrides>;
  foundationPatterns: HalfEdgeAssociationTable<FoundationPattern>;
  framingComponents: Table<FramingComponentEntity>;
  slabComponents: Table<SlabComponentEntity>;
  sheathingComponents: Table<SheathingComponentEntity>;
  datumAbstractions: RWTable<DatumAbstraction>;
  facades: Table<GeneratedFacade>;
  levelLabels: HalfEdgeAssociationTable<Label>;
  facadeSpanStratum: Table<FacadeSpanStratum>;
  facadeSweepStratum: Table<FacadeSweepStratum>;
  wallAssemblyOverrides: HalfEdgeAssociationTable<WallAssemblyOperator>;
  blocks: Table<Block>;
  roofLevelPerimeters: PolyLine2[];
}): WallPath[] {
  const levelRooms = roomsOnLevel(levelId, rooms, roomsByLevel);
  const planarGraphLookupTables = initWallLookupStructures({
    rooms: levelRooms,
    planarGraph,
    roomConnections,
    roomLevelIndices,
    levelLabels,
    facades,
  });

  const { halfEdges, vertexPositions } = planarGraph.planarGraph;

  const halfEdgeToWallPath: WallPath[] = new Array(halfEdges.length / 2);

  const { halfEdgeToFacades } = planarGraphLookupTables;

  for (let i = 0, l = halfEdges.length; i < l; i += 2) {
    const leftHalfEdge = i / 2;
    // if half edge is visited, ignore it
    if (halfEdgeToWallPath[leftHalfEdge]) {
      continue;
    }

    // get opposite half edge & face
    const rightHalfEdge = planarGraphAdjacentHalfEdge(leftHalfEdge);

    const v0 = halfEdges[leftHalfEdge * 2];
    const v1 = halfEdges[leftHalfEdge * 2 + 1];

    const startX = vertexPositions[v0 * 2];
    const startY = vertexPositions[v0 * 2 + 1];

    const endX = vertexPositions[v1 * 2];
    const endY = vertexPositions[v1 * 2 + 1];

    const length = line2Len(startX, startY, endX, endY);

    const leftWallRoom = makeWallSideRoomDescriptor(
      levelId,
      leftHalfEdge,
      planarGraph,
      planarGraphLevelAssociations,
      planarGraphLookupTables,
      blocks,
    );

    const rightWallRoom = makeWallSideRoomDescriptor(
      levelId,
      rightHalfEdge,
      planarGraph,
      planarGraphLevelAssociations,
      planarGraphLookupTables,
      blocks,
    );

    if (!leftWallRoom.room && !rightWallRoom.room) {
      throw new OverlappingRoomsError({ type: "LEVEL", id: levelId }, []);
    }

    const leftRoom = leftWallRoom.room;
    const leftEdgeOpen = !!leftWallRoom.roomConnection;
    const leftEdgeUnWalled = !isWalledRoomEnclosure[leftWallRoom.roomEnclosure];
    const leftFloorAssembly = getRoomFloorAssembly(roomFloorAssemblies, leftWallRoom.room);
    const leftWallFacades = halfEdgeToFacades[leftHalfEdge] ?? [];

    const rightRoom = rightWallRoom.room;
    const rightEdgeOpen = !!rightWallRoom.roomConnection;
    const rightEdgeUnWalled = !isWalledRoomEnclosure[rightWallRoom.roomEnclosure];
    const rightFloorAssembly = getRoomFloorAssembly(roomFloorAssemblies, rightWallRoom.room);
    const rightWallFacades = halfEdgeToFacades[rightHalfEdge] ?? [];

    const wallDescriptor = makeWallDescriptor({
      level: levels[levelId],
      docRooms,
      leftWallRoom,
      leftWallFacades,
      leftFloorAssembly,
      rightWallRoom,
      rightWallFacades,
      rightFloorAssembly,
      halfEdgeIndex: leftHalfEdge,
      foundationSettings,
      foundationPatterns,
      foundationComponents,
      foundationAssemblies,
      foundationAssemblyOverrides,
      framingComponents,
      slabComponents,
      sheathingComponents,
      wallSettings,
      datumAbstractions,
      facadeSpanStratum,
      facadeSweepStratum,
      wallAssemblyOperators: wallAssemblyOverrides,
    });

    let wallCondition = determineWallCondition(leftWallRoom, rightWallRoom);
    if (wallCondition === WallCondition.EXTERIOR_INTERIOR) {
      const isHalfStoryUnderRoofWall = isEdgeHalfStoryUnderRoofWall(
        blocks,
        leftRoom,
        rightRoom,
        roofLevelPerimeters,
        startX,
        startY,
        endX,
        endY,
      );
      if (isHalfStoryUnderRoofWall) {
        wallCondition = WallCondition.INTERIOR_ROOF;
      }
    }

    if (isExteriorWallCondition[wallCondition]) {
      if (wallDescriptor.isCripple) {
        wallCondition = WallCondition.EXTERIOR_CRIPPLE;
      } else if (wallDescriptor.isFurring) {
        wallCondition = WallCondition.INTERIOR_FURRING;
      }
    }

    let wallType: WallType;
    if (leftEdgeOpen || rightEdgeOpen || (leftEdgeUnWalled && rightEdgeUnWalled)) {
      wallType = WallType.VOID;
    } else {
      wallType = WallType.SOLID;
    }

    const wallBaseOffset = deprecatedBaseOffset(wallDescriptor);

    const wallPath = wallPathNew(
      wallType,
      wallCondition,
      wallDescriptor,
      leftHalfEdge,
      leftRoom,
      rightRoom,
      // TODO - remove roomKind from wallPath
      leftWallRoom.roomKind,
      rightWallRoom.roomKind,
      startX,
      startY,
      endX,
      endY,
      length,
      wallBaseOffset,
    );

    halfEdgeToWallPath[leftHalfEdge] = wallPath;

    const oppHalfEdgePath = wallPathOpposite(wallPath);
    halfEdgeToWallPath[rightHalfEdge] = oppHalfEdgePath;
  }

  return halfEdgeToWallPath;
}
